import React, { useState, useEffect } from "react";
import {
  Table,
  Input,
  InputNumber,
  Button,
  Space,
  DatePicker,
  message,
  Popconfirm,
  Spin,
  Divider,
} from "antd";
import dayjs from "dayjs";
import { SearchOutlined } from "@ant-design/icons";
import {
  bulkInsertAbsensi,
  bulkUpdateAbsensi,
  bulkDeleteAbsensi,
  getAbsenKaryawan,
} from "../../api/transaksi/Absensi";

const AbsensiPage = () => {
  const [bulan, setBulan] = useState(dayjs());
  const [isBulanValid, setIsBulanValid] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [loadingTable, setLoadingTable] = useState(true);
  const [loadingRow, setLoadingRow] = useState(null);
  const [loadingAll, setLoadingAll] = useState(false);
  const [deletingRow, setDeletingRow] = useState(null);
  const [deletingAll, setDeletingAll] = useState(false);
  const searchInput = React.useRef(null);

  const [data, setData] = useState([]);

  useEffect(() => {
    loadData();
  }, [bulan]);

  const loadData = async () => {
    setLoadingTable(true);
    try {
      const apiData = await getAbsenKaryawan(bulan.format("YYYY-MM"), "");
      setData(
        apiData.map((item) => ({
          key: item.emp_id_karyawan,
          abs_id_absensi: item.abs_id_absensi ?? null,
          nama: item.emp_nama.trim(),
          total_hadir: Number(item.abs_total_hadir),
          total_ijin: Number(item.abs_total_ijin),
          total_sakit: Number(item.abs_total_sakit),
          total_cuti: Number(item.abs_total_cuti),
          total_alfa: Number(item.abs_total_alfa),
        }))
      );
    } catch (err) {
      console.error(err);
      message.error("Gagal load data");
    } finally {
      setLoadingTable(false);
    }
  };

  const isValidMonth = (val) => {
    if (!val) return false;
    const restrictMonth = process.env.REACT_APP_RESTRICT_MONTH === "true";
    if (!restrictMonth) return true; // jika tidak dibatasi, semua bulan valid
    const now = dayjs();
    const lastMonth = now.subtract(1, "month");
    return val.isSame(now, "month") || val.isSame(lastMonth, "month");
  };

  const handleMonthChange = (val) => {
    setBulan(val);
    const valid = isValidMonth(val);
    setIsBulanValid(valid);
    if (!valid) {
      message.warning("Absensi hanya untuk bulan ini atau bulan sebelumnya.");
    }
  };

  const handleChange = (val, key, field) => {
    setData((d) => d.map((r) => (r.key === key ? { ...r, [field]: val } : r)));
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Cari ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Cari
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const handleSaveRow = async (record) => {
    setLoadingRow(record.key);
    const baseData = {
      abs_employe_id: Number(record.key),
      abs_periode: bulan.format("YYYY-MM"),
      abs_total_hadir: record.total_hadir,
      abs_total_ijin: record.total_ijin,
      abs_total_sakit: record.total_sakit,
      abs_total_cuti: record.total_cuti,
      abs_total_alfa: record.total_alfa,
    };

    try {
      if (record.abs_id_absensi) {
        await bulkUpdateAbsensi([
          {
            ...baseData,
            abs_id_absensi: record.abs_id_absensi,
            abs_updated_by: "admin",
          },
        ]);
      } else {
        await bulkInsertAbsensi([{ ...baseData, abs_created_by: "admin" }]);
      }

      message.success(`Absensi ${record.nama} berhasil disimpan`);

      if (!record.abs_id_absensi) {
        setData((prev) =>
          prev.map((item) =>
            item.key === record.key
              ? { ...item, abs_id_absensi: Date.now() }
              : item
          )
        );
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.message ||
        `Gagal menyimpan absensi ${record.nama}`;
      message.error(errMsg);
    } finally {
      setLoadingRow(null);
    }
  };

  const handleDeleteRow = async (record) => {
    setDeletingRow(record.key);
    try {
      if (record.abs_id_absensi) {
        await bulkDeleteAbsensi([record.abs_id_absensi]);
      }

      const resetData = {
        ...record,
        abs_id_absensi: null,
        total_hadir: 0,
        total_ijin: 0,
        total_sakit: 0,
        total_cuti: 0,
        total_alfa: 0,
      };

      setData((prev) =>
        prev.map((item) => (item.key === record.key ? resetData : item))
      );

      message.success(`Data absensi ${record.nama} berhasil hapus`);
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || `Gagal hapus data ${record.nama}`;
      message.error(errMsg);
    } finally {
      setDeletingRow(null);
    }
  };

  const handleSaveAll = async () => {
    setLoadingAll(true);
    const toInsert = [];
    const toUpdate = [];

    data.forEach((r) => {
      const base = {
        abs_employe_id: Number(r.key),
        abs_periode: bulan.format("YYYY-MM"),
        abs_total_hadir: r.total_hadir,
        abs_total_ijin: r.total_ijin,
        abs_total_sakit: r.total_sakit,
        abs_total_cuti: r.total_cuti,
        abs_total_alfa: r.total_alfa,
      };

      if (r.abs_id_absensi) {
        toUpdate.push({
          ...base,
          abs_id_absensi: r.abs_id_absensi,
          abs_updated_by: "admin",
        });
      } else {
        toInsert.push({ ...base, abs_created_by: "admin" });
      }
    });

    try {
      if (toInsert.length) await bulkInsertAbsensi(toInsert);
      if (toUpdate.length) await bulkUpdateAbsensi(toUpdate);
      message.success("Semua absensi berhasil disimpan");
      const updated = data.map((r) =>
        !r.abs_id_absensi
          ? { ...r, abs_id_absensi: Date.now() + Math.random() * 1000 }
          : r
      );
      setData(updated);
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || "Gagal menyimpan semua absensi";
      message.error(errMsg);
    } finally {
      setLoadingAll(false);
    }
  };

  const handleDeleteAll = async () => {
    setDeletingAll(true);
    const ids = data
      .filter((d) => d.abs_id_absensi)
      .map((d) => d.abs_id_absensi);

    try {
      if (ids.length) await bulkDeleteAbsensi(ids);
      const resetAll = data.map((item) => ({
        ...item,
        abs_id_absensi: null,
        total_hadir: 0,
        total_ijin: 0,
        total_sakit: 0,
        total_cuti: 0,
        total_alfa: 0,
      }));
      setData(resetAll);
      message.success("Semua absensi berhasil dihapus");
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || "Gagal menghapus semua data";
      message.error(errMsg);
    } finally {
      setDeletingAll(false);
    }
  };

  const handleSearch = (keys, confirm, dataIndex) => {
    confirm();
    setSearchText(keys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const columns = [
    {
      title: "NAMA KARYAWAN",
      dataIndex: "nama",
      ...getColumnSearchProps("nama"),
    },
    ...[
      "total_hadir",
      "total_cuti",
      "total_ijin",
      "total_sakit",
      "total_alfa",
    ].map((field) => ({
      title: field.toUpperCase().replace("_", " "),
      dataIndex: field,
      render: (text, record) => (
        <InputNumber
          min={0}
          value={record[field]}
          onChange={(val) => handleChange(val, record.key, field)}
          disabled={!isBulanValid}
        />
      ),
    })),
    {
      title: "AKSI",
      dataIndex: "aksi",
      render: (_, record) => (
        <Space style={{ margin: 1, padding: 2 }}>
          <Button
            type="link"
            loading={loadingRow === record.key}
            onClick={() => handleSaveRow(record)}
            disabled={!isBulanValid}
            style={{ margin: 0, padding: 0 }}
          >
            Simpan
          </Button>
          <Popconfirm
            title="Yakin hapus data ini?"
            onConfirm={() => handleDeleteRow(record)}
            okText="Ya"
            cancelText="Batal"
          >
            <Button
              type="link"
              danger
              loading={deletingRow === record.key}
              disabled={!isBulanValid}
              style={{ margin: 0, padding: 0, paddingLeft: 5 }}
            >
              Hapus
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "0 24px  24px  24px" }}>
      <Divider>Input Data Absen Karyawan</Divider>
      <Space style={{ marginBottom: 16 }}>
        <label>Pilih Bulan: </label>
        <DatePicker
          picker="month"
          value={bulan}
          onChange={handleMonthChange}
          allowClear={false}
        />
        <Button
          type="primary"
          onClick={handleSaveAll}
          disabled={!isBulanValid}
          loading={loadingAll}
        >
          Simpan Semua
        </Button>
        <Popconfirm
          title="Yakin hapus semua data?"
          onConfirm={handleDeleteAll}
          okText="Ya"
          cancelText="Batal"
        >
          <Button danger loading={deletingAll} disabled={!isBulanValid}>
            Hapus Semua
          </Button>
        </Popconfirm>
      </Space>
      <Spin spinning={loadingTable}>
        <Table
          dataSource={data}
          columns={columns}
          pagination={false}
          bordered
          rowKey="key"
        />
      </Spin>
    </div>
  );
};

export default AbsensiPage;
